import { useState } from 'react';
import { Database, Loader2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface SeedDemoDataButtonProps {
  accessToken: string;
  onComplete?: () => void;
  currentUser?: any; // Add currentUser to detect demo environment
}

export function SeedDemoDataButton({ accessToken, onComplete, currentUser }: SeedDemoDataButtonProps) {
  const [seeding, setSeeding] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  // Detect if this is a demo/staging environment
  // Check if consulting firm ID contains 'demo' or if user has demo metadata
  const isDemoEnvironment = currentUser?.consultingFirmId?.toLowerCase().includes('demo') || 
                            currentUser?.firmId?.toLowerCase().includes('demo') ||
                            currentUser?.consultingFirmName?.toLowerCase().includes('demo') ||
                            currentUser?.isDemoUser === true;

  const handleSeedData = async () => {
    if (!confirm('This will create demo data including SOWs, queue items, notifications, and analytics data. Continue?')) {
      return;
    }

    try {
      setSeeding(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/seed-demo-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(
            `Sample data generated`,
            {
              description: `${data.summary.sows} SOWs, ${data.summary.queueItems} queue items, ${data.summary.notifications} notifications, ${data.summary.timeEntries} time entries`,
              duration: 5000,
            }
          );
          onComplete?.();
        } else {
          toast.error(data.message || 'Failed to generate sample data');
        }
      } else {
        toast.error('Failed to generate sample data. Please try again.');
      }
    } catch (error) {
      console.error('Error generating sample data:', error);
      toast.error('An error occurred while generating sample data');
    } finally {
      setSeeding(false);
    }
  };

  const handleCleanupSOWs = async () => {
    if (!confirm('This will remove excess SOWs, keeping only the 4 most recent per company. Continue?')) {
      return;
    }

    try {
      setCleaning(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/cleanup-demo-sows`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(
            `SOWs cleaned up`,
            {
              description: `Removed ${data.totalDeleted} excess SOWs. Kept ${data.totalKept} most recent.`,
              duration: 5000,
            }
          );
          onComplete?.();
        } else {
          toast.error(data.message || 'Failed to clean up SOWs');
        }
      } else {
        toast.error('Failed to clean up SOWs. Please try again.');
      }
    } catch (error) {
      console.error('Error cleaning up SOWs:', error);
      toast.error('An error occurred while cleaning up SOWs');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleSeedData}
        disabled={seeding || cleaning}
        className="gap-2"
      >
        {seeding ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Database className="size-4" />
            Generate Sample Data
          </>
        )}
      </Button>
      
      {/* Only show Cleanup button in demo/staging environments */}
      {isDemoEnvironment && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleCleanupSOWs}
          disabled={seeding || cleaning}
          className="gap-2"
        >
          {cleaning ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Cleaning...
            </>
          ) : (
            <>
              <Trash2 className="size-4" />
              Cleanup SOWs
            </>
          )}
        </Button>
      )}
    </div>
  );
}